import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { get, map, isEmpty } from "lodash";
import classNames from "classnames";
import {
  getMemberBusyStatus,
  getMemberOnlineStatus,
  getSortedChats,
} from "selectors/chat/chat";
import ChatHeader from "../shared/ChatHeader";
import Row from "./Row";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Textarea from "react-textarea-autosize";
import {
  faCheck,
  faCommentPlus,
  faPencil,
  faXmark as faRegularXmark,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faCircle as faSolidCircle,
  faEyeSlash as faSolidEyeSlash,
  faUser,
} from "@fortawesome/pro-solid-svg-icons";
import { toggleChatbar } from "../../../layout/togglebar";
import { setChatStatus } from "../../../actions/chat";
import DropDown from "../../shared/DropDown";
import PageLoading from "components/shared/PageLoading";

const statusOption = {
  busy: "busy",
  online: "online",
  invisible: "invisible",
};

const useCurrentStatus = () => {
  const isOnline = useSelector((state) =>
    getMemberOnlineStatus(state, window?.Preload?.current_member?.id),
  );

  const presence = useSelector((state) =>
    getMemberBusyStatus(state, window?.Preload?.current_member?.id),
  );

  return (
    presence?.type || (isOnline ? statusOption.online : statusOption.invisible)
  );
};

function StatusMessageForm({
  setStatusMessageForm,
}: {
  setStatusMessageForm: (status: boolean) => void;
}) {
  const presence = useSelector((state) =>
    getMemberBusyStatus(state, window?.Preload?.current_member?.id),
  );
  const initialRender = useRef(true);
  const [statusText, setStatusText] = useState(presence?.text || "");
  const currentStatus = useCurrentStatus();
  const [selectedOption, setSelectedOption] = useState(currentStatus);

  const dispatch = useDispatch();

  function submitStatus(status) {
    if (status.text) status.text = status.text.replace(/[\n\r]/g, " ");
    dispatch(setChatStatus({ payload: status }));
  }

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }

    if (selectedOption == statusOption.invisible) {
      submitStatus({ text: statusText, type: statusOption.invisible });
      setStatusMessageForm(false);
    }

    if (
      selectedOption === statusOption.busy &&
      (isEmpty(statusText) ||
        statusText === I18n.t("js.chat.status_text.online.default"))
    ) {
      setStatusText(I18n.t("js.chat.status_text.busy.default"));
    } else if (
      selectedOption === statusOption.online &&
      (isEmpty(statusText) ||
        statusText === I18n.t("js.chat.status_text.busy.default"))
    ) {
      setStatusText(I18n.t("js.chat.status_text.online.default"));
    }
  }, [selectedOption, setSelectedOption]);

  return (
    <div className={classNames("chat__header px-2 flex gap-2", {})}>
      {selectedOption != "invisible" && (
        <div className="flex flex-col w-full">
          <div className="flex gap-0 items-center w-full">
            <div className="grid stack-container w-full h-full">
              <Textarea
                maxLength={100}
                minRows={1}
                maxRows={2}
                value={statusText}
                autoFocus
                onChange={(e) => setStatusText(e.target.value)}
                className={
                  "stack-item grow text-sm rounded rounded-r-none h-full border-neutral resize-none pr-8"
                }
              />
              <button
                className="stack-item btn self-center justify-self-end text-muted py-0"
                onClick={(e) => {
                  e.preventDefault();
                  setStatusText("");
                }}
              >
                <FontAwesomeIcon icon={faRegularXmark} />
              </button>
            </div>
            <button
              className="btn btn-primary btn-sm rounded-l-none h-full"
              onClick={() => {
                submitStatus({ text: statusText, type: selectedOption });
                setStatusMessageForm(false);
              }}
            >
              <FontAwesomeIcon icon={faCheck} />
            </button>
          </div>
          <span className="stack-item justify-self-end self-end mr-8 pb-1 text-[10px] text-muted w-fit h-fit">{`${statusText.length}/100`}</span>
        </div>
      )}
    </div>
  );
}

function CloseButton() {
  return (
    <button
      className="btn btn-sm chat__header_action"
      title={I18n.t("js.chat.close")}
      aria-label={I18n.t("js.chat.close")}
      onClick={toggleChatbar}
    >
      <FontAwesomeIcon icon={faRegularXmark} />
    </button>
  );
}

function HeaderButtons({
  toggleStatusMessageForm,
  connected,
}: {
  toggleStatusMessageForm: () => void;
  connected: boolean;
}) {
  const dispatch = useDispatch();
  const status = useCurrentStatus();

  function startNewChat(e) {
    e.preventDefault();
    dispatch({ type: "chat/NAVIGATE", payload: { route: "newChat" } });
  }

  const presence = useSelector((state) =>
    getMemberBusyStatus(state, window?.Preload?.current_member?.id),
  );
  const statusText = presence?.text || "";

  function submitStatus(type: string) {
    if (status.text) status.text = status.text.replace(/[\n\r]/g, " ");
    dispatch(setChatStatus({ payload: { type, text: statusText } }));
  }

  return (
    <div className="flex">
      {window?.Preload?.current_network.config.show_online_status !==
        "disabled" && (
        <>
          <DropDown.Toggle
            showIcon={false}
            buttonType="normal"
            className="grid stack-container flex gap-1 items-center"
            title={I18n.t(`js.chat.status_text.${status}.label`)}
          >
            <FontAwesomeIcon
              icon={faUser}
              className="stack-item text-xl text-muted"
            />
            <FontAwesomeIcon
              icon={status === "invisible" ? faSolidEyeSlash : faSolidCircle}
              className={classNames(
                "stack-item text-xs justify-self-end self-center -mr-1 h-2 w-2",
                {
                  "chat__status--online": status === "online",
                  "chat__status--busy": status === "busy",
                  "text-muted h-3 w-3": status === "invisible",
                },
              )}
            />
          </DropDown.Toggle>
          <DropDown>
            {map(["online", "busy", "invisible"], (type) => (
              <DropDown.Item
                key={type}
                text={I18n.t(`js.chat.status_text.${type}.label`)}
                iconClassName={classNames({
                  "chat__status--online": type === "online",
                  "chat__status--busy": type === "busy",
                })}
                icon={type === "invisible" ? faSolidEyeSlash : faSolidCircle}
                onSelect={(e) => {
                  e.preventDefault();
                  submitStatus(type);
                }}
                additional={
                  status === type && <FontAwesomeIcon icon={faCheck} />
                }
              />
            ))}
            <DropDown.Item
              text={I18n.t("js.chat.status_text.edit.label")}
              icon={faPencil}
              onSelect={toggleStatusMessageForm}
              dividerTop
              propagateClick
            />
          </DropDown>
        </>
      )}
      <button
        className="btn btn-sm chat__header_action"
        title={I18n.t("js.chat.list.start_new")}
        aria-label={I18n.t("js.chat.list.start_new")}
        onClick={startNewChat}
        disabled={!connected}
      >
        <FontAwesomeIcon icon={faCommentPlus} />
      </button>
    </div>
  );
}

export default function ChatList() {
  const chats = useSelector((state) => getSortedChats(state));
  const connected = useSelector((state) =>
    get(state, ["chat", "session", "connected"]),
  );
  const [showStatusMessageForm, setShowStatusMessageForm] = useState(false);

  function toggleStatusMessageForm() {
    setShowStatusMessageForm(!showStatusMessageForm);
  }

  return (
    <>
      <ChatHeader
        title={I18n.t("js.chat.list.headline")}
        ButtonLeft={!window.isApp ? <CloseButton /> : undefined}
        ButtonRight={
          <HeaderButtons
            toggleStatusMessageForm={toggleStatusMessageForm}
            connected={connected}
          />
        }
      />
      {showStatusMessageForm && (
        <StatusMessageForm setStatusMessageForm={setShowStatusMessageForm} />
      )}
      {isEmpty(chats) ? (
        <div className="p-3">
          <PageLoading />
        </div>
      ) : (
        <div className="chat__list">
          {map(chats, (item, index) => (
            <Row key={index} {...item} />
          ))}
        </div>
      )}
    </>
  );
}
